import { timestampDTOSchema } from '@tempo-io/common-dto';
import { z } from 'zod';
import { findingGroups } from './finding-dto';
import { trackedItemStatusSchema } from './tracked-item-status';

export {
  type TrackedItemStatus,
  trackedItemStatuses,
  trackedItemStatusSchema,
} from './tracked-item-status';

export const humanSettableTrackedItemStatuses = [
  'pinned',
  'assigned',
  'in_motion',
  'completed',
  'dismissed',
] as const;
export type HumanSettableTrackedItemStatus = (typeof humanSettableTrackedItemStatuses)[number];
export const humanSettableTrackedItemStatusSchema = z.enum(humanSettableTrackedItemStatuses);

/**
 * Verification state of a tracked item's grounding snapshot. `unverified` until the deterministic
 * replay engine ever runs against it; the rest are the verifier's own verdicts. Only `unverified` is
 * written today — this is a seam for that later workstream, not yet load-bearing.
 */
export const groundingStatuses = [
  'unverified',
  'holding',
  'patched',
  'resolved',
  'invalidated',
  'needsReview',
] as const;
export type GroundingStatus = (typeof groundingStatuses)[number];
export const groundingStatusSchema = z.enum(groundingStatuses);

/** Whether a status transition was made by a person or written by the pipeline (e.g. a future auto-resolve). */
export const trackedItemActorTypes = ['user', 'system'] as const;
export type TrackedItemActorType = (typeof trackedItemActorTypes)[number];
export const trackedItemActorTypeSchema = z.enum(trackedItemActorTypes);

export const trackedItemDTOSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  loopId: z.number().int().positive().nullable(),
  findingId: z.string(),
  /** null = the whole finding is tracked; set = one specific recommendation inside it. */
  recommendationId: z.string().nullable(),
  status: trackedItemStatusSchema,
  assigneeUserId: z.string().nullable(),
  createdByUserId: z.string(),
  lastActorUserId: z.string().nullable(),
  dueAt: z.string().nullable(),
  note: z.string().nullable(),
  /** Frozen at write time — lets the row render even if the source finding is later retired,
   * capped out of a run, or its run becomes unreadable on a future schema-version bump. */
  findingHeadlineSnapshot: z.string(),
  recommendationTitleSnapshot: z.string().nullable(),
  /** Soft reference only, no FK: the run row this came from is allowed to become unreadable or be
   * pruned without taking the tracked item down with it. */
  sourceRunId: z.string(),
  /** The newest completed run whose findings still carried this item (the finding, or the specific
   * recommendation when one is tracked); null until the first run completed after it was tracked.
   * Refreshed by the pipeline, so "not seen since <date>" is readable off the row. */
  lastSeenRunId: z.string().nullable(),
  lastSeenAt: z.string().nullable(),
  /** Captured at pin time from the finding's own group; the evidence-replay comparator reads this to
   * decide what counts as "resolved" for this tracked item. */
  findingGroup: z.enum(findingGroups).nullable(),
  /** The evidence-replay engine's captured fact contract, copied by value at pin time. Left untyped
   * here (rather than importing findingGroundingSchema) to avoid a dependency the replay workstream
   * doesn't need yet; that engine parses/validates its own shape on read. */
  groundingSnapshot: z.record(z.string(), z.unknown()).nullable(),
  groundingStatus: groundingStatusSchema,
  currentValue: z.union([z.number(), z.string(), z.boolean()]).nullable(),
  lastVerifiedAt: z.string().nullable(),
  lastVerifiedRunId: z.string().nullable(),
  resolutionNote: z.string().nullable(),
  /** Set on the first replay cycle that finds the underlying fact missing; two consecutive
   * occurrences finalize `groundingStatus: 'invalidated'` rather than one bad read doing so. */
  pendingInvalidationSince: z.string().nullable(),
  ...timestampDTOSchema.shape,
});
export type TrackedItemDTO = z.infer<typeof trackedItemDTOSchema>;

export const trackedItemEventDTOSchema = z.object({
  id: z.string(),
  trackedItemId: z.string(),
  organizationId: z.string(),
  fromStatus: trackedItemStatusSchema.nullable(),
  toStatus: trackedItemStatusSchema,
  actorType: trackedItemActorTypeSchema,
  actorUserId: z.string().nullable(),
  note: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  ...timestampDTOSchema.shape,
});
export type TrackedItemEventDTO = z.infer<typeof trackedItemEventDTOSchema>;

export const trackedItemDetailDTOSchema = trackedItemDTOSchema.extend({
  history: z.array(trackedItemEventDTOSchema),
});
export type TrackedItemDetailDTO = z.infer<typeof trackedItemDetailDTOSchema>;

// `agent_tracked`/`auto_resolved`/`superseded` are deliberately excluded — a caller cannot request
// either directly, matching `humanSettableTrackedItemStatuses`.
const trackedItemStatusFields = {
  status: humanSettableTrackedItemStatusSchema,
  assigneeUserId: z.string().min(1).optional(),
  note: z.string().trim().min(1).max(1000).optional(),
  dueAt: z.string().datetime().optional(),
};

const assigneeRequiredWhenAssigned = (body: {
  status: HumanSettableTrackedItemStatus;
  assigneeUserId?: string;
}) => body.status !== 'assigned' || body.assigneeUserId !== undefined;

const ASSIGNEE_REQUIRED = { message: 'assigneeUserId is required when status is assigned' };

/** Transitioning an item that already exists, where the run no longer has to be named. */
export const setTrackedItemStatusBodySchema = z
  .object(trackedItemStatusFields)
  .refine(assigneeRequiredWhenAssigned, ASSIGNEE_REQUIRED);
export type SetTrackedItemStatusBody = z.output<typeof setTrackedItemStatusBodySchema>;

// The finding/recommendation is identified by the URL; `loopId` + `runId` name the specific run the
// caller was looking at when it acted, so the first call for a given finding/recommendation can
// validate it exists and snapshot its headline/title server-side rather than trusting client text.
// Ignored on a subsequent call once the tracked item already exists.
export const createOrTransitionTrackedItemBodySchema = z
  .object({
    loopId: z.number().int().positive().nullable(),
    runId: z.string().min(1),
    ...trackedItemStatusFields,
  })
  .refine(assigneeRequiredWhenAssigned, ASSIGNEE_REQUIRED);
export type CreateOrTransitionTrackedItemBody = z.output<
  typeof createOrTransitionTrackedItemBodySchema
>;

/** Not `createPageDTOSchema`: the service answers `nextCursor: null` on the last page, which the
 * shared generic does not allow. */
export const trackedItemsPageDTOSchema = z.object({
  items: z.array(trackedItemDTOSchema),
  nextCursor: z.string().nullable().optional(),
});
export type TrackedItemsPageDTO = z.infer<typeof trackedItemsPageDTOSchema>;
